import React from 'react'
import { useStoreConfig } from 'config'
import {
    formatPrice,
    getStringAttributeValue,
    getCanonical,
    getFileExtension,
    getFirstImage
} from '../util'
import { getFirstNotEmpty } from '@corratech/pylot-utils'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import { ProductInterface } from '@pylot-data/pylotschema'
import { ProductJsonLd } from './ProductJsonLd'

import Head from 'next/head'
export type ProductProps = {
    product: ProductInterface
    pathname: string
    titleOverride?: string
    descriptionOverride?: string
}

export const Product = ({
    product,
    pathname,
    titleOverride,
    descriptionOverride
}: ProductProps): JSX.Element => {
    const config = useStoreConfig()
    const { variant } = useProductUI(product)
    const ogImageContent = getFirstImage(variant.media_gallery)
    const { seo } = config

    // In some cases, product name and description are provided via non-standard attributes
    const title = getFirstNotEmpty([
        titleOverride,
        product?.meta_title,
        getStringAttributeValue(product, 'name', config),
        seo.default.title
    ])

    const description = getFirstNotEmpty([
        descriptionOverride,
        product?.meta_description,
        product?.short_description?.html,
        seo.default.description
    ])

    const price = formatPrice(
        getStringAttributeValue(product, 'price_amount', config),
        config
    )
    const priceCurrency = getStringAttributeValue(
        product,
        'price_currency',
        config
    )

    const ogImageAlt = 'Product Image'

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="title" content={title} />
                <meta name="keywords" content={String(product.meta_keyword)} />
                <meta name="description" content={String(description)} />

                {/* PRODUCT META */}
                <meta name="product:price:amount" content={price} />
                <meta name="product:price:currency" content={priceCurrency} />

                <link
                    rel="canonical"
                    href={getCanonical(pathname, config.base.url.baseUrl)}
                />
                {/* TWITTER */}
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta
                    property="twitter:domain"
                    content={seo.default.twitter?.handle}
                />
                <meta
                    property="twitter:url"
                    content={getCanonical(pathname, config.base.url.baseUrl)}
                />
                <meta
                    name="twitter:card"
                    content={seo.default.twitter?.cardType}
                />
                <meta name="twitter:image" content={ogImageContent} />
                {/* OPEN GRAPH */}
                <meta
                    property="og:url"
                    content={getCanonical(pathname, config.base.url.baseUrl)}
                />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={ogImageContent} />
                <meta property="og:image:alt" content={ogImageAlt} />
                <meta
                    property="og:image:type"
                    content={`image/${getFileExtension(ogImageContent)}`}
                />

                <meta
                    property="og:site_name"
                    content={seo.default.twitter?.site}
                />
            </Head>
            <ProductJsonLd
                product={product}
                title={title}
                description={description}
            />
        </>
    )
}
